import { Component, OnInit } from "@angular/core";
import { DomSanitizer, Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { getFileExtension } from "../../shared/helper-functions";
import { Mimetype } from "../../shared/mimetype";
import { MomentPipe } from "../../shared/moment.pipe";
import { Path } from "../../shared/path";
import { DocumentData } from "../annotation-controller/document-data.type";
import { CannotPreviewViewerContent, FileViewerConfig } from "../viewer-controller/file-viewer/file-viewer.type";
import { InfoPanelConfig } from "../viewer-controller/viewer-info-panel/viewer-info-panel.types";
import { ViewerTopBarConfig } from "../viewer-controller/viewer-top-bar/viewer-top-bar.component";
import { IdexService } from "app/idex.service";

@Component({
   selector: "app-dropbox-viewer",
   templateUrl: "./dropbox-viewer.component.html",
   styleUrls: ["./dropbox-viewer.component.css"],
})
export class DropboxViewerComponent implements OnInit {
   topBarConfig: ViewerTopBarConfig;
   viewerConfig: FileViewerConfig;
   infoPanelConfig: InfoPanelConfig;

   accessToken: string;
   item: any;
   path: string;
   url: any;
   lastModified: Date;

   constructor(
      private titleService: Title,
      private route: ActivatedRoute,
      private router: Router,
      private sanitizer: DomSanitizer,
      private service: IdexService
   ) { }

   ngOnInit(): void {
      this.accessToken = localStorage.getItem("access_token_dropbox")
      this.route.queryParams.subscribe(params => {
         this.item = JSON.parse(params['item']);
         this.path = this.item.pathDisplay;
         this.titleService.setTitle("zDOX - Dropbox - " + this.item.name);
         this.service.dropboxLink(this.accessToken, this.path,).subscribe(res => {
            if (res) {
               this.url = res.url;
               this.lastModified = new Date();
               this.topBarConfig = this.setTopBarConfig(this.item);
               this.infoPanelConfig = this.setInfoPanelConfig(this.item);
               this.setViewerConfig(this.item);
            }
         });
      });
   }

   //=============================================================================
   // MAPPING DRIVEITEM TO VIEWER CONFIG
   //=============================================================================

   private setTopBarConfig(item: any): ViewerTopBarConfig {
      const topBarConfig: ViewerTopBarConfig = {
         viewMode: "default",
         fileName: item.name,
         lastModifiedTime: new MomentPipe().transform(this.lastModified),
         showBackButton: true,
         showLogo: false,
         showShareButton: false,
         showOpenWithButton: false,
         showDownloadButton: false,
         showToggleInfoPanelButton: false,
      };
      return topBarConfig;
   }

   private setInfoPanelConfig(item: any): InfoPanelConfig {
      const infoPanelConfig: InfoPanelConfig = {
         enableInfoPanel: false,
      };
      return infoPanelConfig;
   }

   private async setViewerConfig(item: any) {
      // PDFTron Viewer
      if (
         [
            "application/pdf",
            "application/vnd.oasis.opendocument.text",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            "application/msword",
            "application/vnd.ms-excel",
            "application/vnd.ms-powerpoint",
            "image/jpeg",
            "image/png",
            "image/tiff",
         ].includes(item.mimeType) &&
         getFileExtension(item.name) !== "csv"
      ) {
         this.viewerConfig = {
            viewerType: "document",
            content: {
               actionType: "dms",
               allowDownloadAndPrint: true,
               fileName: item.name,
               isPermissions: {
                  print: true,
                  edit: false,
                  create: false,
                  delete: false,
                  write: false,
               },
               mimeType: item.mimeType,
               url: this.url,
            } as DocumentData,
         };
      }
      // Image (not supported by PDFTron)
      else if (["image/gif", "image/bmp"].includes(item.mimeType)) {
         this.topBarConfig.showDownloadButton = true;

         this.viewerConfig = {
            viewerType: "image",
            content: this.url,
         };
      }
      // SVG
      else if (item.mimeType === "image/svg+xml" || getFileExtension(item.name) === "svg") {
         this.topBarConfig.showDownloadButton = true;

         const unsafe_content = await fetch(this.url).then((res) => res.text());
         const safe_content = this.sanitizer.bypassSecurityTrustHtml(unsafe_content); // Not really safe 🥲
         this.viewerConfig = {
            viewerType: "svg",
            content: safe_content,
         };
      }
      // Video
      else if (
         item.mimeType.indexOf("video/") === 0 &&
         item.mimeType !== "video/mpeg" &&
         item.mimeType !== "video/avi" &&
         item.mimeType !== "video/msvideo" &&
         item.mimeType !== "video/x-msvideo"
      ) {
         this.topBarConfig.showDownloadButton = true;
         this.viewerConfig = {
            viewerType: "video",
            content: this.url,
         };
      }
      // Audio
      else if (item.mimeType.indexOf("audio/") === 0) {
         this.topBarConfig.showDownloadButton = true;
         this.viewerConfig = {
            viewerType: "audio",
            content: this.url,
         };
      }
      // Plain Text (.txt, .xml)
      else if (item.mimeType.indexOf("text/") === 0 || item.mimeType === "application/xml") {
         this.topBarConfig.showDownloadButton = true;
         const content = await fetch(this.url).then((res) => res.text());
         this.viewerConfig = {
            viewerType: "text",
            content: content,
         };
      }
      // Other/Cannot Preview
      else {
         this.viewerConfig = {
            viewerType: "other",
            content: {
               fileName: item?.name,
               fileSizeByte: item?.size,
               allowDownload: Boolean(true),
               iconPath: Mimetype.getIconGoogleDrive(item),
            } as CannotPreviewViewerContent,
         };
      }
   }

   //=============================================================================
   // HANDLING EMITTED EVENT FROM APP-VIEWER-CONTROLLER
   //=============================================================================

   back() {
      const path = () => {
         if (!this.item) {
            return "/files";
         } else if (this.path) {
            const regex = /^(.*)\/([^.]+(\.([^\/?#]+))+)(\?[^#]*)?(#.*)?$/;
            const match = this.path.match(regex);
            if (match !== null) {
               const { [1]:dirname, [2]:file, [4]:ext } = match;
               this.path = dirname;
            }
            return Path.combine("/dropboxPage", this.path);
         } else {
            return "/dropboxPage";
         }
      };
      this.router.navigate([path()]);
   }

   download() {
      location.href = this.url;
   }

   openWith() {
      console.error("Method not implemented");
   }
}
